/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * regel-planitemfunctie.ts
 * Gegenereerd met Junie (AI), 07-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Subclass voor regels die diensten (planitems) op functie filteren.
 */

import {Filtertoolregel} from '../filtertool-regels';
import type { Planitem as PlanitemType } from '../../../models/planitem';
import type { Functie as FunctieType } from '../../../models/functie';
import type { Personeel as PersoneelType } from '../../../models/personeel';
import { PlanitemFilterbareRegel, FunctieFilterbareRegel, PersoneelFilterbareRegel } from '../filtertool-capabilities';

/**
 * Planitemfunctie-regel met een lijst van Functie-id's. Geldig wanneer er ten minste 1 id is gekozen.
 */
export class FiltertoolPlanitemfunctieRegel extends Filtertoolregel implements PlanitemFilterbareRegel, FunctieFilterbareRegel, PersoneelFilterbareRegel {
    /** Gekozen Functie-id's. */
    functieIds: number[];

    constructor(init?: Partial<Filtertoolregel> & { functieIds?: number[] }) {
        super('planitemfunctie', init as any);
        this.functieIds = Array.isArray(init?.functieIds) ? init.functieIds : [];
    }

    /**
     * Geldig wanneer er minstens één functie is gekozen.
     */
    override get valid(): boolean {
        return this.functieIds?.length > 0;
    }

    /**
     * Serialiseer inclusief functieIds.
     */
    override toObject(): any {
        return {...super.toObject(), functieIds: this.functieIds || []};
    }

    /**
     * Past de functieregel toe op een planitem.
     * We hebben het type planitem, dus dat kunnen we gebruiken
     */
    public filterPlanitem(pi: PlanitemType): boolean {
        return this.functieIds.some(id => pi.functions.includes(id));
    }

    /**
     * Past de functieregel toe op een Functie-item (directe filtering op Functie).
     */
    public filterFunctie(f: FunctieType): boolean {
        return this.functieIds.includes(f.id);
    }

    /**
     * Past de functieregel toe op een Personeel-item. Het personeelslid moet minimaal één van de
     * geselecteerde functies mogen uitvoeren. Hiervoor wordt Personeel.heeftFunctie gebruikt.
     */
    public filterPersoneel(persoon: PersoneelType): boolean {
        // Onbetrouwbare bron: in sommige contexten (bijv. PPI-evaluatie) kan persoon null/undefined zijn
        // of (in edge-cases) nog niet volledig geïnjecteerd. In dat geval matcht deze regel niet.
        if (!this.functieIds?.length || !persoon) {
            return false;
        }
        return this.functieIds.some(functieId => persoon && typeof (persoon as any).heeftFunctie === 'function' && persoon.heeftFunctie(functieId));
    }
}
